import { cn } from '@/lib/utils';

import NextImage, { NextImageProps } from '@/decodo/components/atoms/Image';
import LinkButton from '@/decodo/components/atoms/LinkButton';
import { Text } from '@/decodo/components/atoms/Text';

import IconArrow from '~/icons/arrow-up-right.svg';

export type MenuPromoBannerProps = {
  image?: NextImageProps;
  title: string;
  url: string;
  description?: string;
  onLinkClick?: (
    params: { title: string } | { url: string; keepMenuOpen?: boolean }
  ) => void;
  layout?: 'horizontal' | 'vertical';
};

export function MenuPromoBanner({
  image,
  title,
  description,
  url,
  onLinkClick,
  layout = 'horizontal',
}: MenuPromoBannerProps) {
  const handleClick = (
    e: React.MouseEvent<HTMLAnchorElement>,
    title: string,
    url: string
  ) => {
    const currentPath = window.location.pathname;

    if (url === currentPath) {
      e.preventDefault();
      onLinkClick?.({ title, url });
      return;
    }

    onLinkClick?.({ title, url });
  };

  return (
    <div
      className={cn(
        'bg-brand-100 hover:bg-brand-100/70 inset-0 flex w-full cursor-pointer items-start gap-x-3  rounded p-4 transition-colors duration-100'
      )}
    >
      <div className={cn('flex items-start gap-x-2')}>
        {!!image && <NextImage {...image} height={24} width={24} aria-hidden />}
        <div
          className={cn(
            layout === 'horizontal' && 'flex flex-row items-center gap-x-2'
          )}
        >
          <LinkButton
            variant='text'
            noStyles
            href={url}
            onClick={(e) => handleClick(e, title, url)}
            className='z-[1] overflow-hidden outline-none'
          >
            <Text
              variant='h5'
              classNames={{
                wrapper: 'truncate font-serif select-text text-base-dark-black',
              }}
            >
              {title}
            </Text>
          </LinkButton>
          {!!description && (
            <Text
              variant='body-m'
              color='gray-dark-600'
              classNames={{
                wrapper: cn('relative min-w-[250px] max-w-[400px]'),
              }}
            >
              {description}
            </Text>
          )}
        </div>
      </div>
      <div className='relative ml-auto flex h-6 w-6 shrink-0 items-center justify-center'>
        <IconArrow className='headerLg:h-full headerLg:w-full h-5 w-5 text-gray-500' />
      </div>
    </div>
  );
}
